import {
  type AdminPmTimelineItem,
  ApiError,
  ApiNetworkError,
  type PmGroupKind,
  adminPmConversationGet,
} from '@cortex/web-api/clients/admin/api';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageBubble } from '../components/conversation/MessageBubble.js';
import { PickerCard } from '../components/conversation/PickerCard.js';

/**
 * v1.2 PM-onboarding (PR7/PR8) — drill-down from TenantDetailScreen's PM list.
 *
 * Read-only view of a bound PM's Telegram DM thread with @AcelCortexBot. The
 * data lives on the per-tenant container's DB; the demo admin handler proxies
 * via Bearer PROVISIONER_TOKEN to /admin/internal-pm-conversation/:chatId.
 *
 * The thread is a merged timeline of chat messages and picker cards:
 *   - inbound (PM) message → left, paper bubble
 *   - outbound (agent) message → right, ink bubble
 *   - picker card → right, inset card rendering the Telegram buttons with the
 *     PM's tapped button highlighted in green
 *   - internal [SYSTEM] resume messages are hidden (the PM never saw them; the
 *     picker card already shows the tap).
 *
 * Limit hardcoded to 100 items (server-side); pagination is parked for a later
 * PR. Server returns most-recent-first; UI reverses for natural top-to-bottom
 * reading. The view auto-refreshes every 5s while open so forwarded Telegram
 * messages and picker-card decisions appear without leaving/re-entering the PM.
 */

interface Props {
  slug: string;
  chatId: string;
  groupKind?: PmGroupKind | null;
  onSessionExpired: () => void;
  onBack: () => void;
}

const REFRESH_MS = 5_000;

export function PmConversationScreen({
  slug,
  chatId,
  groupKind = null,
  onSessionExpired,
  onBack,
}: Props) {
  const [items, setItems] = useState<AdminPmTimelineItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);
  const requestSeqRef = useRef(0);

  const loadConversation = useCallback(
    async (mode: 'initial' | 'refresh') => {
      const requestSeq = ++requestSeqRef.current;
      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const rows = await adminPmConversationGet(slug, chatId, groupKind);
        if (requestSeq !== requestSeqRef.current) return;
        // Server returns most-recent-first; reverse to oldest-first for the
        // chat-thread reading order.
        setItems([...rows].reverse());
        setLastLoadedAt(new Date());
      } catch (err) {
        if (requestSeq !== requestSeqRef.current) return;
        if (err instanceof ApiError && err.status === 401) {
          onSessionExpired();
          return;
        }
        if (err instanceof ApiError && err.status === 502) {
          setError("Couldn't reach the tenant container.");
        } else if (err instanceof ApiNetworkError) {
          setError("Couldn't reach the admin server.");
        } else {
          setError('Could not load the conversation.');
        }
      } finally {
        if (requestSeq === requestSeqRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [slug, chatId, groupKind, onSessionExpired],
  );

  useEffect(() => {
    let cancelled = false;
    void loadConversation('initial');
    const interval = window.setInterval(() => {
      if (!cancelled) void loadConversation('refresh');
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      requestSeqRef.current += 1;
      window.clearInterval(interval);
    };
  }, [loadConversation]);

  // Hide internal [SYSTEM] resume messages — the PM never saw them in Telegram.
  const visible = items?.filter((it) => it.type !== 'message' || !it.system) ?? null;

  return (
    <div className="app">
      <header className="app-header" style={{ justifyContent: 'space-between', display: 'flex' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
          <Wordmark />
        </div>
        <button
          type="button"
          onClick={onBack}
          className="btn-secondary"
          style={{ padding: '8px 16px' }}
        >
          ← back
        </button>
      </header>

      <main className="app-main" style={{ overflow: 'auto', padding: 'var(--space-4)' }}>
        <div style={{ maxWidth: 720, margin: '0 auto', width: '100%' }}>
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
              }}
            >
              <div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>conversation</h1>
                <p className="field-help" style={{ marginTop: 'var(--space-1)' }}>
                  <span className="mono">{slug}</span> · {groupKind ? 'group' : 'chat'}{' '}
                  <span className="mono" title={chatId}>
                    …{chatId.slice(-6)}
                  </span>{' '}
                  {groupKind ? `· ${groupLabel(groupKind)} ` : ''}· last 100 messages ·
                  auto-refreshes every 5s
                  {lastLoadedAt ? ` · updated ${lastLoadedAt.toLocaleTimeString()}` : ''}
                </p>
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => loadConversation('refresh')}
                disabled={refreshing}
                style={{ padding: '6px 12px', fontSize: 12 }}
              >
                {refreshing ? 'refreshing…' : 'refresh'}
              </button>
            </div>
          </div>

          {loading && items === null && <p className="field-help">loading conversation…</p>}
          {error && (
            <div
              style={{
                padding: 'var(--space-3)',
                border: '1px solid var(--hairline)',
                borderRadius: 8,
                color: 'var(--muted)',
              }}
            >
              {error}
            </div>
          )}
          {!error && visible !== null && visible.length === 0 && (
            <p className="field-help">no messages yet for this PM.</p>
          )}
          {visible !== null && visible.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {visible.map((item) =>
                item.type === 'picker' ? (
                  <PickerCard key={item.id} picker={item} />
                ) : (
                  <MessageBubble key={item.id} message={item} />
                ),
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function groupLabel(kind: PmGroupKind): string {
  if (kind === 'leasing') return 'Leasing';
  if (kind === 'maintenance') return 'Maintenance';
  if (kind === 'etransfers') return 'E-transfers';
  return 'Admin / Accounting';
}
